import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDeleteProduct } from '@/hooks/useProducts';
import type { Product } from '@/types/product';

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
}

export function DeleteConfirmDialog({ open, onClose, product }: DeleteConfirmDialogProps) {
  const deleteMutation = useDeleteProduct();
  const [error, setError] = useState('');

  if (!open || !product) return null;

  async function handleDelete() {
    if (!product) return;
    setError('');

    try {
      await deleteMutation.mutateAsync(product.productId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg mx-4">
        <h2 className="text-lg font-semibold">Produkt l&ouml;schen</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          M&ouml;chten Sie <span className="font-medium text-foreground">&ldquo;{product.name}&rdquo;</span> wirklich l&ouml;schen?
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Das Produkt wird deaktiviert und ist nicht mehr sichtbar.
        </p>

        {error && (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Wird gel\u00f6scht...' : 'L\u00f6schen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
